import { useEffect, useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  useEntity,
  useRelatedEntities,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import { imcSyncApiRef } from '@internal/plugin-imc-sync';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

const AWS_ACCOUNT_ID = 'temabit.com/aws-account-id';
const AWS_ACCOUNT_STATUS = 'temabit.com/aws-account-status';

/** True when an entity is a discovered `aws-account` Resource. */
const isAwsAccount = (entity: Entity): boolean =>
  entity.kind === 'Resource' &&
  (entity.spec as { type?: string } | undefined)?.type === 'aws-account';

/**
 * Reads the AWS account id from the annotation, falling back to parsing it out
 * of the entity name (the provider names accounts `aws-account-<id>`).
 */
const accountId = (entity: Entity): string =>
  entity.metadata.annotations?.[AWS_ACCOUNT_ID] ??
  entity.metadata.name.replace(/^aws-account-/, '');

/**
 * The curation dialog: a checkbox per discovered account (checked = included).
 * Save persists the UNCHECKED ids as the System's exclusion set. On failure the
 * dialog stays open and surfaces the error rather than silently dropping it.
 */
const EditAccountsDialog = (props: {
  discovered: Entity[];
  excludedIds: Set<string>;
  onClose: () => void;
  onSave: (nextExcluded: string[]) => Promise<void>;
}) => {
  const { discovered, excludedIds, onClose, onSave } = props;
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(discovered.map(accountId).filter(id => !excludedIds.has(id))),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const toggle = (id: string) =>
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const handleSave = async () => {
    const nextExcluded = discovered
      .map(accountId)
      .filter(id => !checked.has(id));
    setSaving(true);
    setError(undefined);
    try {
      await onSave(nextExcluded);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit AWS Accounts</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Check the accounts that are really related to this System. Only checked
          accounts appear on the card and are used by IMC.
        </Typography>
        <FormGroup>
          {discovered.map(account => {
            const id = accountId(account);
            return (
              <FormControlLabel
                key={stringifyEntityRef(account)}
                control={
                  <Checkbox
                    checked={checked.has(id)}
                    onChange={() => toggle(id)}
                    inputProps={{ 'aria-label': id }}
                  />
                }
                label={`${id}${
                  account.metadata.title ? ` (${account.metadata.title})` : ''
                }`}
              />
            );
          })}
        </FormGroup>
        {error && (
          <Typography variant="body2" color="error">
            Could not save: {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * A dedicated System-page card listing the `aws-account` Resources discovered
 * for the System (feature 0009), with per-System **curation** (feature 0011):
 * an Edit button opens a checkbox dialog over the full discovered set; only the
 * checked (non-excluded) accounts render on the card, and the exclusion set is
 * persisted via the `imc-sync` plugin (which IMC also reads).
 *
 * The discovered universe comes from BOTH the `hasPart` relation (the primary
 * System via the Resource's `spec.system`) and the `dependsOn` relation (shared
 * accounts via `spec.dependencyOf`), unioned and deduped by entity ref. The
 * provider still emits every discovered relation (curation is a read-time
 * overlay, model B), so this relation set is the full discovered universe.
 *
 * The card owns its own `<Grid item>` so that when it self-guards to `null`
 * (a System with no discovered aws-account Resources), NO grid slot is emitted
 * and the overview layout stays aligned. When the System HAS discovered
 * accounts but all are excluded, the card still renders (with the Edit action)
 * so the curation is never stranded.
 */
export const SystemAwsAccountsCard = () => {
  const { entity } = useEntity();
  const systemRef = stringifyEntityRef(entity);
  const imcSyncApi = useApi(imcSyncApiRef);

  const { entities: hasPart } = useRelatedEntities(entity, { type: 'hasPart' });
  const { entities: dependsOn } = useRelatedEntities(entity, {
    type: 'dependsOn',
  });

  // The full discovered set (relations), deduped by ref, deterministic order.
  const discovered = useMemo(() => {
    const byRef = new Map<string, Entity>();
    for (const related of [...(hasPart ?? []), ...(dependsOn ?? [])]) {
      if (isAwsAccount(related)) {
        byRef.set(stringifyEntityRef(related), related);
      }
    }
    return [...byRef.values()];
  }, [hasPart, dependsOn]);

  // Persisted exclusions for this System; a local override reflects unsaved-then
  // -saved edits without a refetch.
  const {
    value: loadedExcluded,
    loading,
    error,
  } = useAsync(
    () => imcSyncApi.getAwsAccountExclusions(systemRef),
    [imcSyncApi, systemRef],
  );
  const [savedExcluded, setSavedExcluded] = useState<string[] | undefined>();
  // CatalogEntityPage is reused across `/catalog/:namespace/:kind/:name`, so the
  // card is NOT remounted when navigating between Systems. Clear the local
  // override on a System change, or one System's saved exclusions would override
  // the next System's freshly-loaded state.
  useEffect(() => setSavedExcluded(undefined), [systemRef]);
  const excludedIds = useMemo(
    () => new Set(savedExcluded ?? loadedExcluded ?? []),
    [savedExcluded, loadedExcluded],
  );

  const [editing, setEditing] = useState(false);

  const visible = discovered.filter(a => !excludedIds.has(accountId(a)));

  if (discovered.length === 0) {
    return null;
  }

  // Editing is disabled until the exclusion set has loaded successfully —
  // otherwise a failed load (excludedIds falls back to empty) would let a Save
  // silently overwrite the stored exclusions with an empty set.
  const editingDisabled = loading || Boolean(error);

  const accountsTable = (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Account ID</TableCell>
          <TableCell>Alias</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Lifecycle</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {visible.map(account => {
          const annotations = account.metadata.annotations ?? {};
          const lifecycle = (
            account.spec as { lifecycle?: string } | undefined
          )?.lifecycle;
          return (
            <TableRow key={stringifyEntityRef(account)}>
              <TableCell>{accountId(account)}</TableCell>
              <TableCell>
                <EntityRefLink
                  entityRef={account}
                  title={account.metadata.title}
                />
              </TableCell>
              <TableCell>{annotations[AWS_ACCOUNT_STATUS] ?? '—'}</TableCell>
              <TableCell>{lifecycle ?? '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  let body: JSX.Element;
  if (loading) {
    body = <Progress />;
  } else if (error) {
    // Curation could not load: show the discovered accounts uncurated, warn,
    // and keep editing disabled so nothing can be saved over unknown state.
    body = (
      <>
        <Typography variant="body2" color="error" gutterBottom>
          Could not load account curation — showing all discovered accounts;
          editing is disabled.
        </Typography>
        {accountsTable}
      </>
    );
  } else if (visible.length === 0) {
    body = (
      <Typography variant="body2" color="textSecondary">
        All accounts hidden — click Edit to show accounts for this System.
      </Typography>
    );
  } else {
    body = accountsTable;
  }

  return (
    <Grid item xs={12}>
      <InfoCard
        title="AWS Accounts"
        action={
          <Button
            color="primary"
            size="small"
            disabled={editingDisabled}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        }
      >
        {body}
      </InfoCard>
      {editing && (
        <EditAccountsDialog
          discovered={discovered}
          excludedIds={excludedIds}
          onClose={() => setEditing(false)}
          onSave={async nextExcluded => {
            await imcSyncApi.setAwsAccountExclusions(systemRef, nextExcluded);
            setSavedExcluded(nextExcluded);
            setEditing(false);
          }}
        />
      )}
    </Grid>
  );
};
